/**
 * f11.6: presentation facts about the validation-warning codes. The RULES are
 * the server's only (BR-F11.6-7); this file never decides whether a warning
 * applies -- it only says how one reads and which cell it points at.
 */

import type { EngineWarning, ValidationWarning, ValidationWarningCode } from '@/api/client';

export interface CatalogEntry {
  label: string;
  /** The grid cell the warning outlines. */
  field: 'probability' | 'sales_rate_override' | 'element_code' | 'overwrite_fixed_service';
  order: number;
}

export const VALIDATION_WARNING_CATALOG: Record<ValidationWarningCode, CatalogEntry> = {
  P1_PROBABILITY_NOT_100: { label: 'P1 not at 100%', field: 'probability', order: 1 },
  SALES_RATE_MISSING: { label: 'FTE without sales rate', field: 'sales_rate_override', order: 2 },
  RESOURCE_BU_UNMAPPED: { label: 'Unmapped Resource BU', field: 'element_code', order: 3 },
  PROBABILITY_OUT_OF_RANGE: { label: 'Probability outside 0–100%', field: 'probability', order: 4 },
  FIXED_SERVICE_NOT_OVERRIDDEN: { label: 'Fixed Service not overridden', field: 'overwrite_fixed_service', order: 5 },
};

export const CHECK_INPUT_GROUP = 'Check input';
export const CALCULATION_GROUP = 'Calculation';

const ENGINE_RATE_MISSING = 'RATE_MISSING';

/** The warnings, in the fixed catalogue order. */
export function orderedWarnings(warnings: readonly ValidationWarning[]): ValidationWarning[] {
  return [...warnings].sort(
    (a, b) => VALIDATION_WARNING_CATALOG[a.code].order - VALIDATION_WARNING_CATALOG[b.code].order,
  );
}

/** The message a given cell should carry, joined when several warnings point at it. */
export function warningForField(warnings: readonly ValidationWarning[], field: CatalogEntry['field']): string | undefined {
  const messages = orderedWarnings(warnings)
    .filter((w) => w.field === field)
    .map((w) => w.message);
  return messages.length ? messages.join('\n') : undefined;
}

/** SALES_RATE_MISSING also outlines the headcount cells of its months. */
export function warningForHeadcount(warnings: readonly ValidationWarning[], month: number): string | undefined {
  const hit = warnings.find((w) => w.code === 'SALES_RATE_MISSING' && w.period_months.includes(month));
  return hit?.message;
}

/**
 * Q-f11.6-4: an engine RATE_MISSING is not repeated when the line's
 * SALES_RATE_MISSING already covers it. The engine collapses a run of months
 * into one warning keyed by the run's first month, so that month decides.
 */
export function visibleEngineWarnings(
  engine: readonly EngineWarning[],
  warnings: readonly ValidationWarning[],
): EngineWarning[] {
  const covered = new Set(warnings.filter((w) => w.code === 'SALES_RATE_MISSING').flatMap((w) => w.period_months));
  return engine.filter(
    (w) => !(w.code === ENGINE_RATE_MISSING && w.period_month != null && covered.has(w.period_month)),
  );
}
